import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import Decimal from 'decimal.js';
import { ChartData } from 'funds-model';
import { makeChangeRanges } from '../SelectFunds/QuotesChangeRange';
import { useFundsQuotesLastDateApi } from './useFundsQuotesLastDateApi';
import { useFundsQuotesChartPricingApi } from './useFundsQuotesChartPricingApi';

interface UseFundSmallChartDataParams {
  fundId: number;
  unitCategory: string;
  fromDate?: string;
  changeRateKey?: string;
  changeFromFund?: number;
}

interface UseFundSmallChartDataResult {
  chartData?: ChartData;
  isLoading: boolean;
  change?: Decimal;
}

export function useFundSmallChartData({
  fundId,
  unitCategory,
  fromDate,
  changeRateKey,
  changeFromFund,
}: UseFundSmallChartDataParams): UseFundSmallChartDataResult {
  const [ranges, setRanges] = useState(makeChangeRanges());

  const lastDate = useFundsQuotesLastDateApi(fundId, unitCategory, !fromDate);

  useEffect(() => {
    if (lastDate.data) {
      setRanges(makeChangeRanges(DateTime.fromISO(lastDate.data)));
    }
  }, [lastDate.data]);

  const actualFromDate = fromDate || ranges.find(e => e.key === changeRateKey)?.fromDate;
  const readyForChartApiCall = !!fromDate || !!lastDate.data;

  const { data: chartData, isLoading: chartLoading } = useFundsQuotesChartPricingApi(
    fundId, unitCategory, actualFromDate, readyForChartApiCall,
  );

  const getChange = (): Decimal | undefined => {
    if (changeFromFund != null) {
      return new Decimal(changeFromFund);
    }
    if (chartData?.change != null) {
      return new Decimal(chartData.change).times(100);
    }
    return undefined;
  };

  const change = getChange();

  const isLoading = lastDate.isLoading || chartLoading;

  return { chartData, isLoading, change };
}
